/**
 * ClassifiedsPreviewSection - Preview of Classifieds on Welcome Page
 * Shows featured property and vehicle listings
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Home, Car, MapPin, Bed, Bath, Calendar, Gauge, 
  ArrowRight, Sparkles, Plus
} from 'lucide-react';
import api from '../../lib/api';

const ClassifiedsPreviewSection = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const res = await api.get('/classifieds?limit=6&sort=newest');
      setListings(res.data.listings || []);
    } catch (error) {
      console.error('Error fetching classifieds:', error);
    }
    setLoading(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-JM', {
      style: 'currency',
      currency: 'JMD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const ListingCard = ({ listing }) => (
    <Card 
      className="group cursor-pointer overflow-hidden bg-slate-900/50 border-slate-700/50 hover:border-[#00A86B]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#00A86B]/10"
      onClick={() => navigate(`/classifieds/${listing.id}`)}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        {listing.cover_image ? (
          <img 
            src={listing.cover_image} 
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-slate-800 flex items-center justify-center">
            {listing.category === 'property' ? (
              <Home className="w-12 h-12 text-slate-600" />
            ) : (
              <Car className="w-12 h-12 text-slate-600" />
            )}
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-2">
          {listing.featured && (
            <Badge className="bg-[#FFD300] text-black font-bold text-xs">
              <Sparkles className="w-3 h-3 mr-1" />
              Featured
            </Badge>
          )}
          {listing.is_new && (
            <Badge className="bg-[#00A86B] text-white font-bold text-xs animate-pulse">
              NEW
            </Badge>
          )}
        </div>
        
        {/* Category Badge */}
        <div className="absolute top-2 right-2">
          <Badge className="bg-black/70 text-white text-xs capitalize">
            {listing.category === 'property' ? (
              <><Home className="w-3 h-3 mr-1" /> {listing.listing_type}</>
            ) : (
              <><Car className="w-3 h-3 mr-1" /> Vehicle</>
            )}
          </Badge>
        </div>
        
        {/* Price */}
        <div className="absolute bottom-2 left-2 bg-black/80 backdrop-blur-sm px-3 py-1 rounded-lg">
          <span className="text-[#FFD300] font-bold">{formatPrice(listing.price)}</span>
          {listing.listing_type === 'rent' && <span className="text-slate-400 text-xs">/mo</span>}
        </div>
      </div>
      
      <CardContent className="p-3">
        <h3 className="font-semibold text-white text-sm truncate group-hover:text-[#00A86B] transition-colors">
          {listing.title}
        </h3>
        
        <div className="flex items-center gap-1 text-slate-400 text-xs mt-1">
          <MapPin className="w-3 h-3" />
          <span className="truncate">{listing.town}, {listing.parish}</span>
        </div>
        
        {/* Quick Stats */}
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
          {listing.category === 'property' ? (
            <>
              {listing.bedrooms !== null && (
                <span className="flex items-center gap-1"><Bed className="w-3 h-3" />{listing.bedrooms}</span>
              )}
              {listing.bathrooms !== null && (
                <span className="flex items-center gap-1"><Bath className="w-3 h-3" />{listing.bathrooms}</span>
              )}
            </>
          ) : (
            <>
              {listing.year && (
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{listing.year}</span>
              )}
              {listing.mileage && (
                <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{(listing.mileage/1000).toFixed(0)}k km</span>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section className="py-16 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
          <div>
            <Badge className="bg-[#00A86B]/20 text-[#00A86B] mb-3">
              <Sparkles className="w-4 h-4 mr-1" />
              NEW
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Property & Vehicle <span className="text-[#FFD300]">Classifieds</span>
            </h2>
            <p className="text-slate-400 mt-2">Buy, sell, or rent properties and vehicles across Jamaica</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button 
              variant="outline" 
              className="border-[#00A86B] text-[#00A86B] hover:bg-[#00A86B] hover:text-white"
              onClick={() => navigate('/classifieds/post')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Post Ad
            </Button>
            <Button 
              className="bg-[#00A86B] hover:bg-[#00A86B]/90"
              onClick={() => navigate('/classifieds')}
            >
              Browse All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
        
        {/* Listings Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-slate-800/50 rounded-xl animate-pulse">
                <div className="aspect-[4/3] bg-slate-700 rounded-t-xl" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-slate-700 rounded w-3/4" />
                  <div className="h-3 bg-slate-700 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 bg-slate-900/50 rounded-2xl border border-slate-800">
            <div className="flex justify-center gap-4 mb-4">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center">
                <Home className="w-8 h-8 text-slate-600" />
              </div>
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center">
                <Car className="w-8 h-8 text-slate-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Listings Yet</h3>
            <p className="text-slate-400 mb-6">Be the first to post a property or vehicle ad!</p>
            <Button 
              className="bg-[#FFD300] hover:bg-[#FFD300]/90 text-black"
              onClick={() => navigate('/classifieds/post')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Post Your First Ad
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {listings.slice(0, 6).map(listing => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
        
        {/* Category Quick Links */}
        <div className="grid grid-cols-3 gap-4 mt-8">
          <button 
            onClick={() => navigate('/classifieds?tab=property_sale')}
            className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-[#00A86B]/50 transition group"
          >
            <Home className="w-8 h-8 text-[#00A86B] mx-auto mb-2 group-hover:scale-110 transition" />
            <p className="text-white font-medium">Property Sale</p>
            <p className="text-xs text-slate-400">Houses, Land, Commercial</p>
          </button>
          <button 
            onClick={() => navigate('/classifieds?tab=property_rent')}
            className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-[#00A86B]/50 transition group"
          >
            <Home className="w-8 h-8 text-[#FFD300] mx-auto mb-2 group-hover:scale-110 transition" />
            <p className="text-white font-medium">Property Rent</p>
            <p className="text-xs text-slate-400">Apartments, Houses</p>
          </button>
          <button 
            onClick={() => navigate('/classifieds?tab=vehicles')}
            className="p-4 bg-slate-800/50 rounded-xl border border-slate-700 hover:border-[#00A86B]/50 transition group"
          >
            <Car className="w-8 h-8 text-blue-400 mx-auto mb-2 group-hover:scale-110 transition" />
            <p className="text-white font-medium">Vehicles</p>
            <p className="text-xs text-slate-400">Cars, Vans, Trucks</p>
          </button>
        </div>
      </div>
    </section>
  );
};

export default ClassifiedsPreviewSection;
